import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  useGetFaqBySlugQuery,
  useAddFaqMutation,
  useDeleteFaqMutation,
} from 'api/workshopApi';

const FaqSection = () => {
  const { slug } = useParams();
  const { data, isLoading, isError, error } = useGetFaqBySlugQuery(slug);
  const [addFaq] = useAddFaqMutation();
  const [deleteFaq] = useDeleteFaqMutation();

  const [faqList, setFaqList] = useState([]);
  const [newFaq, setNewFaq] = useState({ question: '', answer: '' });

  const noFaqFound = isError && error?.data?.message?.includes('No FAQ found');

  useEffect(() => {
    if (data?.data?.qa_pairs && Array.isArray(data.data.qa_pairs)) {
      const formattedFaqs = data.data.qa_pairs.map(([question, answer, id]) => ({
        question,
        answer,
        id,
      }));
      setFaqList(formattedFaqs);
    } else {
      setFaqList([]);
    }
  }, [data]);

  const handleChange = (field, value) => {
    setNewFaq((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const res = await addFaq({ workshopSlug: slug, ...newFaq }).unwrap();
      setFaqList((prev) => [res, ...prev]);
      setNewFaq({ question: '', answer: '' });
      alert('FAQ added successfully!');
    } catch (error) {
      alert('Failed to add FAQ.');
    }
  };

  const handleDelete = async (qaId) => {
    try {
      await deleteFaq({ workshopSlug: slug, qaId }).unwrap();
      setFaqList((prev) => prev.filter((faq) => faq.id !== qaId));
      alert('FAQ deleted successfully!');
    } catch {
      alert('Failed to delete FAQ.');
    }
  };

  if (isLoading) return <p className="text-center py-10">Loading FAQs...</p>;

  if (isError && !noFaqFound)
    return <p className="text-center py-10 text-red-600">Error loading FAQs.</p>;

  return (
    <section className="w-full max-w-4xl mx-auto mt-10 p-4 sm:p-6 md:p-8 border rounded-lg shadow bg-white">
      <h2 className="text-xl sm:text-2xl font-semibold mb-6 text-center md:text-left">
        Workshop FAQs
      </h2>

      {/* CREATE FAQ */}
      <form
        onSubmit={handleCreate}
        className="border p-4 rounded-lg mb-10 space-y-4 bg-gray-50"
      >
        <div>
          <label className="block font-semibold mb-1 text-sm sm:text-base">
            Question
          </label>
          <input
            type="text"
            value={newFaq.question}
            onChange={(e) => handleChange('question', e.target.value)}
            className="border p-2 rounded w-full text-sm sm:text-base"
            placeholder="Enter question"
            required
          />
        </div>

        <div>
          <label className="block font-semibold mb-1 text-sm sm:text-base">
            Answer
          </label>
          <textarea
            value={newFaq.answer}
            onChange={(e) => handleChange('answer', e.target.value)}
            className="border p-2 rounded w-full text-sm sm:text-base"
            placeholder="Enter answer"
            rows={4}
            required
          />
        </div>

        <button
          type="submit"
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors w-full sm:w-auto"
        >
          Add FAQ
        </button>
      </form>

      {/* LIST FAQ */}
      {noFaqFound || faqList.length === 0 ? (
        <div className="text-center text-gray-600 text-sm sm:text-base">
          No FAQs available. Add the first one above!
        </div>
      ) : (
        faqList.map((faq, idx) => {
          const safeQuestion =
            typeof faq.question === 'string' ? faq.question.trim() : '';
          const safeAnswer =
            typeof faq.answer === 'string' ? faq.answer.trim() : '';

          return (
            <div
              key={faq.id}
              className="border p-4 rounded-lg mb-6 space-y-2 bg-gray-50"
            >
              <h3 className="font-bold text-base sm:text-lg">FAQ {idx + 1}</h3>
              <p className="text-sm sm:text-base">
                <strong>Q:</strong> {safeQuestion}
              </p>
              <p className="text-sm sm:text-base">
                <strong>A:</strong> {safeAnswer}
              </p>
              <button
                onClick={() => handleDelete(faq.id)}
                className="bg-red-600 text-white px-4 py-2 rounded mt-2 hover:bg-red-700 transition-colors w-full sm:w-auto"
              >
                Delete
              </button>
            </div>
          );
        })
      )}
    </section>
  );
};

export default FaqSection;
